import React, { useState } from 'react';
import { createCardGroup } from '../api/learning-cards';
import {
    GROUP_VISIBILITY,
    GROUP_VISIBILITY_LABELS,
    CARD_SIDE_TYPES,
    CARD_SIDE_TYPE_LABELS,
    DEFAULT_CARD_SIDE_TYPES
} from '../constants/learning-cards';

function CreateGroupModal({ show, onClose, onGroupCreated }) {
    const [formData, setFormData] = useState({
        group_name: '',
        description: '',
        visibility: GROUP_VISIBILITY.PRIVATE,
        card_side_types: [...DEFAULT_CARD_SIDE_TYPES]
    });

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleCardSideTypeChange = (index, value) => {
        setFormData(prev => ({
            ...prev,
            card_side_types: prev.card_side_types.map((type, i) =>
                i === index ? value : type
            )
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.group_name.trim()) {
            setError('Group name is required');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const result = await createCardGroup(formData);

            if (result.success) {
                setFormData({
                    group_name: '',
                    description: '',
                    visibility: GROUP_VISIBILITY.PRIVATE,
                    card_side_types: [...DEFAULT_CARD_SIDE_TYPES]
                });

                if (onGroupCreated) {
                    onGroupCreated(result.data);
                }

                onClose();
            } else {
                setError(result.message || 'Failed to create group');
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Create group error:', err);
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <>
            <div
                className="modal-backdrop fade show"
                style={{ zIndex: 1040 }}
                onClick={onClose}
            ></div>

            <div
                className="modal fade show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ zIndex: 1050 }}
            >
                <div
                    className="modal-dialog modal-dialog-centered"
                    role="document"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Create New Flashcard Group</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Close"
                            ></button>
                        </div>

                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} noValidate>
                                <div className="mb-3">
                                    <label htmlFor="group_name" className="form-label">
                                        Group Name
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="group_name"
                                        value={formData.group_name}
                                        onChange={handleChange}
                                        disabled={loading}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="description" className="form-label">
                                        Description (Optional)
                                    </label>
                                    <textarea
                                        className="form-control"
                                        id="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        disabled={loading}
                                        rows="3"
                                    ></textarea>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Card Display Types</label>
                                    <div className="row">
                                        <div className="col-6">
                                            <label className="form-label small">First Side</label>
                                            <select
                                                className="form-select"
                                                value={formData.card_side_types[0]}
                                                onChange={(e) => handleCardSideTypeChange(0, e.target.value)}
                                                disabled={loading}
                                            >
                                                {Object.entries(CARD_SIDE_TYPE_LABELS).map(([value, label]) => (
                                                    <option key={value} value={value}>
                                                        {label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label small">Second Side</label>
                                            <select
                                                className="form-select"
                                                value={formData.card_side_types[1]}
                                                onChange={(e) => handleCardSideTypeChange(1, e.target.value)}
                                                disabled={loading}
                                            >
                                                {Object.entries(CARD_SIDE_TYPE_LABELS).map(([value, label]) => (
                                                    <option key={value} value={value}>
                                                        {label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-text">
                                        Choose how each side of your cards should be displayed.
                                        Japanese type includes special formatting for furigana and reading pairs.
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="visibility" className="form-label">
                                        Visibility
                                    </label>
                                    <select
                                        className="form-select"
                                        id="visibility"
                                        value={formData.visibility}
                                        onChange={handleChange}
                                        disabled={loading}
                                    >
                                        {Object.entries(GROUP_VISIBILITY_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary w-100"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Creating...
                                        </>
                                    ) : (
                                        'Create Group'
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default CreateGroupModal;